import {
  deleteV1CompaniesCompanyIdEquipmentId,
  deleteV1CompaniesCompanyIdEquipmentTypesId,
  deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentId,
  getV1CompaniesCompanyIdEquipmentId,
  getV1CompaniesCompanyIdEquipmentTypeahead,
  GetV1CompaniesCompanyIdEquipmentTypeaheadData,
  getV1CompaniesCompanyIdEquipmentTypesId,
  getV1CompaniesCompanyIdEquipmentTypesTypeahead,
  GetV1CompaniesCompanyIdEquipmentTypesTypeaheadData,
  getV1CompaniesCompanyIdJobsJobIdEquipmentTypeahead,
  GetV1CompaniesCompanyIdJobsJobIdEquipmentTypeaheadData,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipment,
  GetV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentData,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentId,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypeahead,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypes,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypesTypeahead,
  GetV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypesTypeaheadData,
  getV1CompaniesIdEquipment,
  GetV1CompaniesIdEquipmentData,
  getV1CompaniesIdEquipmentTypes,
  patchV1CompaniesCompanyIdEquipmentId,
  patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentId,
  postV1CompaniesCompanyIdEquipment,
  postV1CompaniesCompanyIdEquipmentTypes,
  postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipment,
  postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypes,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { useCallback } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { CompanyBasic } from '~hooks/useCompany/models';
import { Filters } from '~pages/Settings/Administration/Equipment/hooks/useEquipmentFilters';
import { EquipmentFormValues } from '~pages/Settings/Administration/EquipmentManagement/EquipmentForm';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  Equipment,
  EquipmentTypeahead,
  EquipmentTypeItem,
  EquipmentTypeTypeahead,
} from './models';

interface CreateEquipmentProps {
  equipment: Equipment;
  companyId?: string;
  managedCompanyId?: string;
}

interface CreateEquipmentByCompanyIdProps {
  equipment: EquipmentFormValues;
  companyId?: string;
  managedCompanyId?: string;
}

interface CreateEquipmentTypeByCompanyProps {
  equipmentType: EquipmentTypeItem;
  companyId: string;
  managedCompanyId?: string;
}

interface GetEquipmentTypesByCompanyIdProps {
  companyId: string;
  managedCompanyId?: string;
  link?: PaginationLink;
  cursor?: string;
  limit?: number;
  signal?: AbortSignal;
}
interface GetEquipmentTypesByCompanyIdTypeaheadProps {
  companyId: string;
  managedCompanyId?: string;
  limit?: number;
  query?: string;
  cursor?: string;
}

interface DeleteEquipmentProps {
  equipmentId: string;
  companyId?: string;
  managedCompanyId?: string;
}
interface DeleteEquipmentTypeProps {
  equipmentTypeId: string;
}
export interface GetEquipmentByCompanyIdProps {
  companyId: string;
  managedCompanyId?: string;
  link?: PaginationLink;
  cursor?: string;
  limit?: number;
  dispatchable?: boolean;
  shared?: boolean;
  internal?: boolean;
  query?: string;
  filters?: Filters;
  signal?: AbortSignal;
}

interface GetEquipmentByCompanyIdTypeaheadProps {
  companyId: string;
  managedCompanyId?: string;
  limit?: number;
  query?: string;
  cursor?: string;
  dispatchable?: boolean;
  shared?: boolean;
}

interface GetJobEquipmentTypeaheadProps {
  companyId: string;
  jobId: string;
  limit?: number;
  query?: string;
  cursor?: string;
}

export default function useEquipment() {
  const { userStore } = useStores();
  const selectedCompanyId = getEffectiveUserCompanyId(userStore);

  const getEquipmentByCompanyId = useCallback(
    async ({
      companyId,
      managedCompanyId,
      link,
      cursor,
      shared,
      internal,
      limit,
      dispatchable,
      query: searchQuery,
      filters,
      signal,
    }: GetEquipmentByCompanyIdProps) => {
      const query:
        | GetV1CompaniesIdEquipmentData['query']
        | GetV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentData['query'] =
        {
          'page[limit]': limit || MAX_API_PAGE_SIZE,
        };
      if (link && cursor) {
        query[`page[${link}]`] = cursor;
      }
      if (!isNil(internal)) {
        query['filter[internal]'] = internal;
      } else if (!isNil(shared)) {
        query['filter[shared]'] = shared;
      }
      if (!isNil(dispatchable)) {
        query['filter[dispatchable]'] = dispatchable;
      }
      if (searchQuery) {
        query['search[datagrid]'] = searchQuery;
      }

      if (!isNil(filters?.equipmentTypes)) {
        query['filter[equipment_type_ids]'] = filters.equipmentTypes;
      }

      if (!isNil(filters?.senderCompanies)) {
        query['filter[sender_company_ids]'] = filters.senderCompanies;
      }

      if (!isNil(filters?.labels)) {
        query['filter[label_ids]'] = filters.labels;
      }

      // internal/external visibility options were removed — the Internal/Vendor Trucks tabs
      // send filter[internal] directly, and combining it with filter[shared] is rejected.
      if (filters?.visibility?.includes('dispatchable')) {
        query['filter[dispatchable]'] = true;
      }

      try {
        const response = managedCompanyId
          ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipment({
              path: { 'company-id': companyId, 'managed-company-id': managedCompanyId },
              query,
              signal,
            })
          : await getV1CompaniesIdEquipment({
              path: { 'company-id': companyId },
              query,
              signal,
            });

        return {
          data: response.data.data.map(Equipment.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.equipment.failed_to_fetch'),
        );
        throw error;
      }
    },
    [],
  );

  const getEquipmentByCompanyIdTypeahead = useCallback(
    async ({
      companyId,
      managedCompanyId,
      limit,
      query: searchQuery,
      cursor,
      dispatchable,
      shared,
    }: GetEquipmentByCompanyIdTypeaheadProps) => {
      const query: GetV1CompaniesCompanyIdEquipmentTypeaheadData['query'] = {
        'page[limit]': limit || MAX_API_PAGE_SIZE,
      };

      if (searchQuery) {
        query['search[query]'] = searchQuery;
      }

      if (cursor) {
        query['page[after]'] = cursor;
      }

      if (!isNil(shared)) {
        query['filter[shared]'] = shared;
      }
      if (!isNil(dispatchable)) {
        query['filter[dispatchable]'] = dispatchable;
      }

      try {
        const response = managedCompanyId
          ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypeahead(
              {
                path: { 'company-id': companyId, 'managed-company-id': managedCompanyId },
                query,
              },
            )
          : await getV1CompaniesCompanyIdEquipmentTypeahead({
              path: { 'company-id': companyId },
              query,
            });

        const parsedData = response.data.data.map(EquipmentTypeahead.parse);
        const pagination = extractPagination(response);
        return { data: parsedData, pagination };
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.equipment.failed_to_fetch'),
        );
        throw error;
      }
    },
    [],
  );

  const getJobEquipmentTypeahead = useCallback(
    async ({
      companyId,
      jobId,
      limit,
      query: searchQuery,
      cursor,
    }: GetJobEquipmentTypeaheadProps) => {
      const query: GetV1CompaniesCompanyIdJobsJobIdEquipmentTypeaheadData['query'] = {
        'page[limit]': limit || MAX_API_PAGE_SIZE,
      };

      if (searchQuery) {
        query['search[query]'] = searchQuery;
      }

      if (cursor) {
        query['page[after]'] = cursor;
      }

      try {
        const response = await getV1CompaniesCompanyIdJobsJobIdEquipmentTypeahead({
          path: { 'company-id': companyId, 'job-id': jobId },
          query,
        });

        const parsedData = response.data.data.map(EquipmentTypeahead.parse);
        const pagination = extractPagination(response);
        return { data: parsedData, pagination };
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.equipment.failed_to_fetch'),
        );
        throw error;
      }
    },
    [],
  );

  const createEquipmentByCompanyId = useCallback(
    async ({
      equipment,
      companyId,
      managedCompanyId,
    }: CreateEquipmentByCompanyIdProps) => {
      const effectiveCompanyId = companyId || (equipment.company as CompanyBasic).id;
      if (!equipment.type?.id) {
        throw new Error('Equipment type is required to create equipment');
      }

      const body = Equipment.deparseCreate({
        name: equipment.name,
        externalId: equipment.externalId ?? null,
        licenseNumber: equipment.licenseNumber ?? null,
        isNotActive: equipment.isNotActive ?? false,
        type: { id: equipment.type.id },
        accountingId: equipment.accountingId ?? null,
        zone: equipment.zone ?? null,
        homeBase: equipment.homeBase
          ? {
              id: equipment.homeBase.id ?? null,
              siteId: equipment.homeBase.siteId ?? null,
            }
          : null,
        labels: equipment.labels ?? [],
        overweightPermitNumber: equipment.overweightPermitNumber ?? null,
        maxGrossWeight: equipment.maxGrossWeight ?? null,
        ownedByAccount: equipment.ownedByAccount,
        shareName: equipment.shareName ?? null,
        shareTicketMatchId: equipment.shareTicketMatchId ?? null,
      });

      try {
        const response = managedCompanyId
          ? await postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipment({
              path: {
                'company-id': effectiveCompanyId,
                'managed-company-id': managedCompanyId,
              },
              body,
            })
          : await postV1CompaniesCompanyIdEquipment({
              path: { 'company-id': effectiveCompanyId || selectedCompanyId },
              body,
            });

        return Equipment.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.equipment.failed_to_create'),
        );
        throw error;
      }
    },
    [selectedCompanyId],
  );

  const createEquipmentTypeByCompanyId = useCallback(
    async ({
      companyId,
      managedCompanyId,
      equipmentType,
    }: CreateEquipmentTypeByCompanyProps) => {
      try {
        const body = EquipmentTypeItem.deparse(equipmentType);
        const response = managedCompanyId
          ? await postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypes({
              path: {
                'company-id': companyId,
                'managed-company-id': managedCompanyId,
              },
              body,
            })
          : await postV1CompaniesCompanyIdEquipmentTypes({
              path: { 'company-id': companyId },
              body,
            });

        const createdEquipmentType = EquipmentTypeItem.parse(response.data.data);

        return createdEquipmentType;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.equipment_types.failed_to_create'),
        );
        throw error;
      }
    },
    [],
  );

  const updateEquipment = useCallback(
    async ({ equipment, companyId, managedCompanyId }: CreateEquipmentProps) => {
      try {
        const body = Equipment.deparseUpdate(equipment);
        const response = managedCompanyId
          ? await patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentId({
              path: {
                'company-id': companyId!,
                'managed-company-id': managedCompanyId,
                id: equipment.id,
              },
              body,
            })
          : await patchV1CompaniesCompanyIdEquipmentId({
              path: {
                'company-id': companyId || selectedCompanyId,
                id: equipment.id,
              },
              body,
            });
        return Equipment.parse(response.data.data);
      } catch (error) {
        // re-throw 422 errors so forms can show the invalid fields
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.equipment.failed_to_update'),
        );
        throw error;
      }
    },
    [selectedCompanyId],
  );

  const deleteEquipment = useCallback(
    async ({ equipmentId, companyId, managedCompanyId }: DeleteEquipmentProps) => {
      try {
        if (managedCompanyId) {
          await deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentId({
            path: {
              'company-id': companyId!,
              'managed-company-id': managedCompanyId,
              id: equipmentId,
            },
          });
        } else {
          await deleteV1CompaniesCompanyIdEquipmentId({
            path: {
              'company-id': selectedCompanyId,
              id: equipmentId,
            },
          });
        }
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.equipment.failed_to_delete'),
        );
      }
    },
    [selectedCompanyId],
  );

  const getEquipmentTypeById = useCallback(
    async (equipmentTypeId: string) => {
      try {
        const response = await getV1CompaniesCompanyIdEquipmentTypesId({
          path: {
            'company-id': selectedCompanyId,
            id: equipmentTypeId,
          },
        });
        return EquipmentTypeItem.parse(response.data.data);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.equipment_types.failed_to_fetch'),
        );
        throw error;
      }
    },
    [selectedCompanyId],
  );

  const getEquipmentTypesByCompanyId = useCallback(
    async ({
      companyId,
      managedCompanyId,
      link,
      cursor,
      limit = MAX_API_PAGE_SIZE,
      signal,
    }: GetEquipmentTypesByCompanyIdProps) => {
      try {
        const query = {
          'page[limit]': limit,
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
        };

        const response = managedCompanyId
          ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypes({
              path: { 'company-id': companyId, 'managed-company-id': managedCompanyId },
              query,
              signal,
            })
          : await getV1CompaniesIdEquipmentTypes({
              path: { 'company-id': companyId },
              query,
              signal,
            });

        return {
          data: response.data.data.map(EquipmentTypeItem.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.equipment_types.failed_to_fetch'),
        );
        throw error;
      }
    },
    [],
  );

  const getEquipmentTypesByCompanyIdTypeahead = useCallback(
    async (options: GetEquipmentTypesByCompanyIdTypeaheadProps) => {
      // A missing company id leaves the `{company-id}` path placeholder
      // unsubstituted (`/v1/companies/%7Bcompany-id%7D/...`), which always 404s
      // and floods the HORIZON-FE-1RY Sentry bucket. Nothing to fetch.
      if (!options.companyId) {
        return { data: [], pagination: undefined };
      }

      const params:
        | GetV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypesTypeaheadData['query']
        | GetV1CompaniesCompanyIdEquipmentTypesTypeaheadData['query'] = {
        'page[limit]': options.limit || MAX_API_PAGE_SIZE,
      };
      if (options?.query) {
        params['search[query]'] = options.query;
      }

      if (options?.cursor) {
        params['page[after]'] = options.cursor;
      }

      try {
        const response = options.managedCompanyId
          ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypesTypeahead(
              {
                path: {
                  'company-id': options.companyId,
                  'managed-company-id': options.managedCompanyId,
                },
                query: params,
              },
            )
          : await getV1CompaniesCompanyIdEquipmentTypesTypeahead({
              path: { 'company-id': options.companyId },
              query: params,
            });

        const formatted = response.data.data.map((equipmentType) => {
          return EquipmentTypeTypeahead.parse(equipmentType);
        });
        const pagination = extractPagination(response);

        return { data: formatted, pagination };
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.equipment_types.failed_to_fetch'),
        );
        throw error;
      }
    },
    [],
  );

  const getEquipmentById = useCallback(
    async (equipmentId: string, companyId?: string, managedCompanyId?: string) => {
      try {
        const effectiveCompanyId = companyId || selectedCompanyId;

        const response = managedCompanyId
          ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentId({
              path: {
                'company-id': effectiveCompanyId,
                'managed-company-id': managedCompanyId,
                id: equipmentId,
              },
            })
          : await getV1CompaniesCompanyIdEquipmentId({
              path: { 'company-id': selectedCompanyId, id: equipmentId },
            });

        return Equipment.parse(response.data.data);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.equipment.failed_to_fetch'),
        );
        throw error;
      }
    },
    [selectedCompanyId],
  );

  const deleteEquipmentType = useCallback(
    async ({ equipmentTypeId }: DeleteEquipmentTypeProps) => {
      try {
        await deleteV1CompaniesCompanyIdEquipmentTypesId({
          path: {
            'company-id': selectedCompanyId,
            id: equipmentTypeId,
          },
        });
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.equipment_types.failed_to_delete'),
        );
      }
    },
    [selectedCompanyId],
  );

  return {
    createEquipmentByCompanyId,
    createEquipmentTypeByCompanyId,
    deleteEquipment,
    deleteEquipmentType,
    getEquipmentByCompanyId,
    getEquipmentByCompanyIdTypeahead,
    getEquipmentById,
    getEquipmentTypeById,
    getEquipmentTypesByCompanyId,
    getEquipmentTypesByCompanyIdTypeahead,
    getJobEquipmentTypeahead,
    updateEquipment,
  };
}
